import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Button,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Fingerprint as FingerprintIcon } from '@mui/icons-material';
import GirviPageShell from '../common/GirviPageShell';
import baseURL from '../../baseURL';
import axios from 'axios';
import { receiptService } from '../../services/receiptService';
import { formatArticleMasterRate } from '../../utils/articleMasterRate';

const InterestCalculation = () => {
  const [ledgers, setLedgers] = useState([]);
  const [selectedLedger, setSelectedLedger] = useState(null);
  const ledgerCode = selectedLedger?.code ?? '';
  const [mode, setMode] = useState('date');
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().split('T')[0]);
  const [months, setMonths] = useState('12');
  const [loadingLedgers, setLoadingLedgers] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fingerprint states
  const [fingerprintScanning, setFingerprintScanning] = useState(false);
  const [scannedPledges, setScannedPledges] = useState([]);
  const [scannedCustomerName, setScannedCustomerName] = useState('');
  const [scannedCustomerCode, setScannedCustomerCode] = useState('');
  const [showCalcOptions, setShowCalcOptions] = useState(false);
  const [masterArticles, setMasterArticles] = useState([]);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${baseURL}/articles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(data)) setMasterArticles(data);
        else if (Array.isArray(data?.data)) setMasterArticles(data.data);
      } catch (_) {}
    };
    fetchArticles();
  }, []);

  const fetchLedgers = useCallback(async () => {
    try {
      setLoadingLedgers(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/ledgers?type=Customer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch ledgers');
      const data = await response.json();
      const list = data.data || data.ledgers || data || [];
      setLedgers(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching ledgers:', error);
      setSnackbar({ open: true, message: 'Error loading ledgers', severity: 'error' });
    } finally {
      setLoadingLedgers(false);
    }
  }, []);

  useEffect(() => {
    fetchLedgers();
  }, [fetchLedgers]);

  const formatDate = (d) => {
    if (!d) return '—';
    const x = new Date(d);
    return isNaN(x.getTime()) ? '—' : x.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const runCalculation = async (code) => {
    try {
      setCalculating(true);
      setResult(null);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ ledgerCode: code, mode });
      if (mode === 'date') params.append('asOnDate', asOnDate);
      else params.append('months', months);
      const response = await fetch(
        `${baseURL}/cash-balance/interest-calculation?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Calculation failed');
      }
      const data = await response.json();
      setResult(data.data);
    } catch (error) {
      console.error('Error calculating interest:', error);
      setSnackbar({ open: true, message: error.message || 'Error calculating interest', severity: 'error' });
    } finally {
      setCalculating(false);
    }
  };

  // Manual calculate (select ledger → calculate)
  const handleCalculate = async () => {
    if (!selectedLedger || !ledgerCode) {
      setSnackbar({ open: true, message: 'Please select a ledger', severity: 'warning' });
      return;
    }
    if (mode === 'date' && !asOnDate) {
      setSnackbar({ open: true, message: 'Please select date', severity: 'warning' });
      return;
    }
    if (mode === 'months' && (!months || parseInt(months, 10) < 1)) {
      setSnackbar({ open: true, message: 'Please enter number of months (≥ 1)', severity: 'warning' });
      return;
    }
    await runCalculation(ledgerCode);
  };

  // Fingerprint scan → fetch all active pledges for matched customer
  const handleFingerprintScan = async () => {
    try {
      setFingerprintScanning(true);
      setScannedPledges([]);
      setScannedCustomerName('');
      setScannedCustomerCode('');
      setShowCalcOptions(false);
      setResult(null);

      const result = await receiptService.searchPledgeByFingerprint(60);

      if (result.matches && result.matches.length > 0) {
        const activeMatches = result.matches.filter(
          p => p.status !== 'RELEASED' && p.loanDetails?.released !== true
        );

        if (activeMatches.length > 0) {
          const customerCode = activeMatches[0]?.customer?.code;
          const customerName = `${activeMatches[0]?.customer?.firstName || ''} ${activeMatches[0]?.customer?.lastName || ''}`.trim();

          if (customerCode) {
            // Auto-select ledger in the dropdown
            const matchedLedger = ledgers.find(l => l.code === customerCode);
            setSelectedLedger(matchedLedger || {
              code: customerCode,
              firstName: activeMatches[0].customer?.firstName,
              lastName: activeMatches[0].customer?.lastName
            });

            // Fetch ALL active pledges for this customer
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${baseURL}/pledges/client/${customerCode}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const allPledges = resp?.data?.pledges || [];
            const activePledges = allPledges.filter(
              p => p.status !== 'RELEASED' && !p.loanDetails?.released
            );

            setScannedPledges(activePledges);
            setScannedCustomerName(customerName);
            setScannedCustomerCode(customerCode);

            if (activePledges.length === 0) {
              setSnackbar({ open: true, message: 'Fingerprint matched but no active pledges found.', severity: 'warning' });
            } else {
              setSnackbar({ open: true, message: `Found ${activePledges.length} active pledge(s) for ${customerName}`, severity: 'success' });
            }
          } else {
            setSnackbar({ open: true, message: 'No customer code in matched pledge.', severity: 'warning' });
          }
        } else {
          setSnackbar({ open: true, message: 'No active pledges found. All matches are released.', severity: 'warning' });
        }
      } else {
        setSnackbar({ open: true, message: 'No matching pledges found. Use manual search.', severity: 'warning' });
      }
    } catch (error) {
      console.error('Fingerprint scan error:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to scan fingerprint', severity: 'error' });
    } finally {
      setFingerprintScanning(false);
    }
  };

  // Calculate interest for scanned customer
  const handleScannedCalculate = async () => {
    if (!scannedCustomerCode) return;
    if (mode === 'date' && !asOnDate) {
      setSnackbar({ open: true, message: 'Please select date', severity: 'warning' });
      return;
    }
    if (mode === 'months' && (!months || parseInt(months, 10) < 1)) {
      setSnackbar({ open: true, message: 'Please enter number of months (≥ 1)', severity: 'warning' });
      return;
    }
    await runCalculation(scannedCustomerCode);
  };

  return (
    <>
      <GirviPageShell
        title="Interest Calculation"
        subtitle="Accounting · Tools"
        chipLabel="Accounting"
        maxWidth="xl"
      >
        {/* Search Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Autocomplete
                  value={selectedLedger}
                  onChange={(e, newValue) => {
                    setSelectedLedger(newValue);
                    setScannedPledges([]);
                    setScannedCustomerCode('');
                    setScannedCustomerName('');
                    setShowCalcOptions(false);
                    setResult(null);
                  }}
                  options={ledgers}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : [option.firstName, option.lastName].filter(Boolean).join(' ') || option.code || ''
                  }
                  isOptionEqualToValue={(option, value) => (option?.code || option) === (value?.code || value)}
                  loading={loadingLedgers}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ledger Name"
                      placeholder="Type to search ledger..."
                    />
                  )}
                  noOptionsText="No ledger found"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={fingerprintScanning ? <CircularProgress size={20} /> : <FingerprintIcon />}
                  onClick={handleFingerprintScan}
                  disabled={fingerprintScanning}
                  sx={{ height: '56px' }}
                >
                  {fingerprintScanning ? 'Scanning...' : 'Scan Fingerprint'}
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
                    <FormControlLabel value="date" control={<Radio />} label="Date" />
                    <FormControlLabel value="months" control={<Radio />} label="Months" />
                  </RadioGroup>
                </Box>
              </Grid>
              {mode === 'date' && (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="As on Date"
                    type="date"
                    value={asOnDate}
                    onChange={(e) => setAsOnDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}
              {mode === 'months' && (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Number of months"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={months}
                    onChange={(e) => setMonths(e.target.value)}
                  />
                </Grid>
              )}
              {/* Manual calculate button — only when no fingerprint scan is active */}
              {scannedPledges.length === 0 && (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCalculate}
                    disabled={calculating || !selectedLedger}
                    startIcon={calculating ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {calculating ? 'Calculating...' : 'Calculate'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Scanned Pledges Section — shown after fingerprint scan */}
        {scannedPledges.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pledges for {scannedCustomerName} ({scannedPledges.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket No</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Principal Amount</TableCell>
                      <TableCell align="right">Rate (%)</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scannedPledges.map((pledge) => (
                      <TableRow key={pledge._id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {pledge.loanDetails?.ticketNo}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(pledge.date)}</TableCell>
                        <TableCell align="right">
                          ₹{Number(pledge.loanDetails?.principalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell align="right">{formatArticleMasterRate(masterArticles, pledge, '—')}</TableCell>
                        <TableCell>{pledge.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleScannedCalculate}
                  disabled={calculating}
                  startIcon={calculating ? <CircularProgress size={20} color="inherit" /> : null}
                  size="large"
                >
                  {calculating ? 'Calculating...' : 'Calculate Interest'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Interest Calculation Results */}
        {result && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Interest Calculation — {result.ledgerName}
                {result.mode === 'date' && ` (as on ${result.asOnDate})`}
                {result.mode === 'months' && ` (${result.months} months from pledge date)`}
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Document</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Principal</TableCell>
                      <TableCell align="right">Interest</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.items && result.items.length > 0 ? (
                      result.items.map((row) => (
                        <TableRow key={row.ticketNo}>
                          <TableCell>Pawn Ticket : {row.ticketNo}</TableCell>
                          <TableCell>{formatDate(row.pledgeDate)}</TableCell>
                          <TableCell align="right">₹{Number(row.principal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell align="right">₹{Number(row.interest).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No active pledges for this ledger
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {result.items && result.items.length > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="h6">
                    <strong>Total Interest:</strong> ₹{Number(result.totalInterest || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </GirviPageShell>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InterestCalculation;
